import React, { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormFeedback,
  FormText,
  Row,
  Col,
  Container,
  Label,
  Input,
  Button,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { createDeliveryUser, updateDeliveryUser, updateUserApi } from "../../api/user";
import { DRIVER_VEHICLE_OPTIONS } from "../../utils/driverVehicleTypes";
import {
  BILLING_DOC_OPTIONS,
  billingDocLabel,
  pendingBillingDocTypeChange,
} from "../../utils/billingDocTypes";
import { CHILE_BANKS, resolveChileBankName } from "../../utils/chileBanks";
import { banksForCountry } from "../../views/adminView/settings/sections/banks";
import { useCountry } from "../../context/CountryContext";
import { AuthService } from "../../services/authservice";
import { RUT_STATE, rutErrorKey, validateRut } from "../../utils/rut";
import { toast } from '../../utils/toast';

export default function CreateDeliveryBeartrack({
  edit,
  defaultData,
  closeModal,
  cerrarModal,
  fetch,
  setConfirmationModal,
  setPackagesCreated,
  user,
}) {
  //console.log("datos del delivery para editar: ", user);
  const { t } = useTranslation();
  const { countryCode } = useCountry();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Bancos por país de operación. Chile: nómina canónica cerrada (el backend traduce el
  // nombre a código SBIF). Otros países: nómina del selector del operador.
  const bankOptions =
    countryCode === "CL"
      ? CHILE_BANKS.map((b) => ({ value: b.name, label: b.name }))
      : (banksForCountry(countryCode) ?? []).map((n) => ({ value: n, label: n }));

  const titles = {
    form1: t("Datos personales"),
    form2: t("Datos para pagos"),
  };
  //logica steps
  const handleNextStep = () => {
    setStep((prevStep) => prevStep + 1);
  };

  // end -logica steps

  const methods = useForm({
    mode: "onSubmit",
    // Al editar, `bank` es un campo plano del form pero en `user` vive anidado en
    // `bankData.bank`; se preselecciona el nombre canónico (resolviendo el alias viejo).
    // Si el valor guardado no se reconoce, queda vacío y el admin vuelve a elegir.
    defaultValues: edit
      ? { ...user, bank: resolveChileBankName(user?.bankData?.bank) || "" }
      : {},
  });

  useEffect(() => {
    if (edit) {
      //console.log("en el useEffect");
    }
  }, [edit, user, methods]);

  // El RUT del repartidor es la llave de pago del alta: mientras tenga contenido y no
  // pase el módulo 11, el paso 2 no deja crear/editar (el backend revalida igual).
  // Vacío se rige por la obligatoriedad de siempre.
  // El formulario es multipaís (ver banksForCountry arriba): fuera de Chile este campo
  // lleva el documento local (NIT, cédula), así que el módulo 11 chileno solo aplica en CL.
  const esChile = countryCode === "CL";
  const rutCheck = validateRut(methods.watch("rutPayPerson"));
  const [rutTouched, setRutTouched] = useState(false);
  const rutBlocked = esChile && rutCheck.state !== RUT_STATE.EMPTY && !rutCheck.ok;
  // El DV equivocado se avisa al tiro (es definitivo); "incompleto" solo tras salir del
  // campo, para no marcar en rojo mientras se escribe.
  const rutError =
    esChile &&
    (rutCheck.state === RUT_STATE.BAD_DV || (rutTouched && rutCheck.state === RUT_STATE.INCOMPLETE))
      ? rutErrorKey(rutCheck)
      : null;
  const RUT_ERROR_ID = "rut-pay-person-error";

  // Cambio factura↔boleta ya guardado que todavía no rige (rige desde el domingo
  // siguiente al cambio). Sin `billingDocTypeChanges` (backend anterior) no se muestra.
  const pendingDocChange = edit ? pendingBillingDocTypeChange(user?.billingDocTypeChanges) : null;
  const pendingDocChangeDate = pendingDocChange
    ? pendingDocChange.effectiveWeekStart.slice(8, 10) + "/" + pendingDocChange.effectiveWeekStart.slice(5, 7)
    : null;

  const ErrorMessage = ({ msg }) => (
    <FormFeedback className="d-block">{msg}</FormFeedback>
  );

  const FormDivider = ({ className }) => (
    <div className={`w-full border-t border-gray-300 my-2 ${className}`} />
  );
  
  const submitValues = useCallback(
    async (values) => {
      if (rutBlocked) return; // defensa extra: el botón ya queda deshabilitado
      setLoading(true);
      const toastId = toast.loading(t("Guardando cambios..."));
      const accessToken = await AuthService.getValidAccessToken();

      if (accessToken) {
        //console.log("datos accessToken::::", accessToken);
        values.token = accessToken;
      }

      try {
        if (edit) {
          await updateDeliveryUser(values.id, values);
          // El tipo de vehículo y el documento tributario NO viajan en el multipart v2;
          // se persisten aparte vía PATCH /users/:id (Axios v1), en UNA sola llamada con
          // los campos que cambiaron. Si falla, no rompe el guardado del resto.
          const patch = {};
          const initialVehicle = user?.vehicleType ?? null;
          const nextVehicle = values.vehicleType || null;
          if (nextVehicle && nextVehicle !== initialVehicle) patch.vehicleType = nextVehicle;

          const initialDoc = user?.billingDocType ?? null;
          const nextDoc = values.billingDocType || null;
          if (nextDoc && nextDoc !== initialDoc) patch.billingDocType = nextDoc;

          if (Object.keys(patch).length > 0) {
            const res = await updateUserApi({
              userId: user?._id || user?.id || values.id,
              values: patch,
            });
            if (res?.error) {
              // 403: solo un administrador del operador puede cambiar el documento
              // tributario. El PATCH es uno solo, así que tampoco se guardó lo que iba con
              // él (el tipo de vehículo); los datos del multipart sí quedaron guardados.
              const forbidden = res.error?.statusCode === 403 && Boolean(patch.billingDocType);
              toast.error(
                forbidden
                  ? patch.vehicleType
                    ? t("Se guardaron los demás datos, pero no el documento tributario ni el tipo de vehículo: solo un administrador del operador puede cambiar el documento tributario.")
                    : t("Se guardaron los demás datos, pero no el documento tributario: solo un administrador del operador puede cambiar el documento tributario.")
                  : t("El usuario se guardó, pero no se pudieron actualizar el tipo de vehículo o el documento tributario."),
              );
            }
          }
          toast.update(toastId, {
            appearance: "success",
            render: t("Usuario actualizado exitosamente"),
            type: "success",
            isLoading: false,
            autoClose: 2000,
            position: "bottom-center",
            autoDismiss: true,
          });
        } else {
          await createDeliveryUser(values);
          toast.update(toastId, {
            appearance: "success",
            render: t("Usuario creado exitosamente"),
            type: "success",
            isLoading: false,
            autoClose: 2000,
            position: "bottom-center",
            autoDismiss: true,
          });
        }
        setLoading(false);
        closeModal(false);
        fetch(true); //comentado para pruebas
      } catch (error) {
        // console.error("Error al guardar los cambios:", error);
        setLoading(false);
        toast.update(toastId, {
          appearance: "error",
          render: t("Error al guardar los cambios: ") + error.message,
          type: "error",
          isLoading: false,
          autoClose: 2000,
          position: "bottom-center",
          autoDismiss: true,
        });
      }
    },
    [closeModal, edit, fetch, user, rutBlocked]
  );

  const getCurrentTitle = () => {
    switch (step) {
      case 1:
        return titles.form1;
      case 2:
        return titles.form2;
      default:
        return titles.form1;
    }
  };

  const isForm1Complete =
    methods.watch("name") &&
    methods.watch("email") &&
    methods.watch("phone")
    // methods.watch("carDocument") &&
    // methods.watch("carId")
    ;

  const isForm2Complete =
    methods.watch("namePayPerson") &&
    methods.watch("rutPayPerson") &&
    !rutBlocked &&
    methods.watch("accountType") &&
    methods.watch("bank");

  return (
    <div>
      <h1 className="text-black font-semibold text-xl mb-0 mt-0">
        {t(getCurrentTitle())}
      </h1>
      <FormDivider />
      <Container className="mt-0">
        <Form onSubmit={methods.handleSubmit(submitValues)}>
          {step === 1 && (
            <>
              <Row className="mt-0">
                <Col xs="12" className="">
                <Label className="forms-input-title">{t("Nombre")}</Label>
                  <Controller
                    name="name"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        className="p-2 px-3 forms-custom-input"
                        placeholder={t("Pedro Gonzalez")}
                        defaultValue=""
                      />
                    )}
                    rules={{
                      required: t("Ingrese el Nombre del vendedor"),
                    }}
                  />
                  {methods.formState.errors?.name && (
                    <ErrorMessage
                      msg={methods.formState.errors?.name?.message}
                    />
                  )}
                </Col>
                <Col xs="12" className="">
                <Label className="forms-input-title">{t("Correo electrónico")}</Label>
                  <Controller
                    name="email"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="email"
                        className="p-2 px-3 forms-custom-input"
                        placeholder={t("pedro@otsl.cl")}
                        defaultValue=""
                      />
                    )}
                    rules={{
                      required: t("Ingrese el Correo electrónico del vendedor"),
                    }}
                  />
                  {methods.formState.errors?.email && (
                    <ErrorMessage
                      msg={methods.formState.errors?.email?.message}
                    />
                  )}
                </Col>
                <Col xs="12">
                <Label className="forms-input-title">{t("Teléfono")}</Label>
                  <Controller
                    name="phone"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        className="p-2 px-3 forms-custom-input"
                        placeholder={t("+56987654321")}
                        defaultValue=""
                      />
                    )}
                    rules={{
                      required: t("Ingrese el Teléfono del vendedor"),
                    }}
                  />
                  {methods.formState.errors?.phone && (
                    <ErrorMessage
                      msg={methods.formState.errors?.phone?.message}
                    />
                  )}
                </Col>
                {/* Tipo de vehículo: solo en edición. El admin puede fijar/cambiar el
                    tipo (persiste vía PATCH /users/:id). No se permite volver a "Sin
                    configurar" una vez elegido. */}
                {edit && (
                  <Col xs="12">
                    <Label className="forms-input-title">{t("Tipo de vehículo")}</Label>
                    <Controller
                      name="vehicleType"
                      control={methods.control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="select"
                          className="p-2 px-3 forms-custom-input"
                          value={field.value || ""}
                        >
                          <option value="" disabled>
                            {t("Sin configurar")}
                          </option>
                          {DRIVER_VEHICLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {t(opt.labelKey)}
                            </option>
                          ))}
                        </Input>
                      )}
                    />
                  </Col>
                )}
                {/* Documento tributario: solo en edición. Cambia el pago del repartidor
                    (persiste vía PATCH /users/:id). El placeholder "Sin documento" queda
                    deshabilitado: una vez elegido no se puede volver a "sin documento". */}
                {edit && (
                  <Col xs="12">
                    <Label className="forms-input-title">{t("Documento tributario")}</Label>
                    <Controller
                      name="billingDocType"
                      control={methods.control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="select"
                          className="p-2 px-3 forms-custom-input"
                          value={field.value || ""}
                        >
                          <option value="" disabled>
                            {t("Sin documento")}
                          </option>
                          {BILLING_DOC_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {t(opt.label)}
                            </option>
                          ))}
                        </Input>
                      )}
                    />
                    <FormText className="d-block">
                      {/* La primera elección (perfil sin tipo) aplica de inmediato: si no,
                          esas semanas no se podrían pagar. */}
                      {user?.billingDocType
                        ? t("El cambio rige desde el domingo siguiente; las semanas ya cerradas o con documento mantienen su tipo.")
                        : t("Como es la primera elección, aplica también a las semanas que aún no tienen documento.")}
                    </FormText>
                    {pendingDocChange && (
                      <FormText className="d-block">
                        {t("Cambio programado: {{from}} → {{to}} desde el dom {{fecha}}", {
                          from: billingDocLabel(pendingDocChange.from, t),
                          to: billingDocLabel(pendingDocChange.to, t),
                          fecha: pendingDocChangeDate,
                        })}
                      </FormText>
                    )}
                  </Col>
                )}
                <h1 className="text-black font-semibold text-xl mb-0 mt-4">
                  {t("Documentos del vehículo")}
                </h1>
                <FormDivider></FormDivider>
                <Col xs="12">
                  <Label className="forms-input-title">
                    {t("Licencia de conducir")}
                  </Label>
                  <Controller
                    name="driveLicense"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="file"
                        className="p-2 px-3 forms-custom-input"
                        accept=".pdf,.png,.jpg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.size <= 5242880) {
                            field.onChange(e);
                          } else {
                            methods.setError("driveLicense", {
                              type: "manual",
                              message:
                                t("El archivo debe ser PDF, PNG o JPG y menor a 5 MB"),
                            });
                          }
                        }}
                      />
                    )}
                    rules={{
                      required: t("Por favor suba la licencia de conducir"),
                    }}
                  />
                  {methods.formState.errors?.driveLicense && (
                    <ErrorMessage
                      msg={methods.formState.errors?.driveLicense?.message}
                    />
                  )}
                </Col>
                <Col xs="12">
                  <Label className="forms-input-title">
                    {t("Permiso de circulación")}
                  </Label>
                  <Controller
                    name="carDocument"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="file"
                        className="p-2 px-3 forms-custom-input"
                        accept=".pdf,.png,.jpg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.size <= 5242880) {
                            field.onChange(e);
                          } else {
                            methods.setError("carDocument", {
                              type: "manual",
                              message:
                                t("El archivo debe ser PDF, PNG o JPG y menor a 5 MB"),
                            });
                          }
                        }}
                      />
                    )}
                    rules={{
                      required: t("Por favor suba el permiso de circulación"),
                    }}
                  />
                  {methods.formState.errors?.carDocument && (
                    <ErrorMessage
                      msg={methods.formState.errors?.carDocument?.message}
                    />
                  )}
                </Col>
                <Col xs="12">
                  <Label className="forms-input-title">
                    {t("Padrón del vehículo")}
                  </Label>
                  <Controller
                    name="carId"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="file"
                        className="p-2 px-3 forms-custom-input"
                        accept=".pdf,.png,.jpg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.size <= 5242880) {
                            field.onChange(e);
                          } else {
                            methods.setError("carId", {
                              type: "manual",
                              message:
                                t("El archivo debe ser PDF, PNG o JPG y menor a 5 MB"),
                            });
                          }
                        }}
                      />
                    )}
                    rules={{
                      required: t("Por favor suba el padrón del vehículo"),
                    }}
                  />
                  {methods.formState.errors?.carId && (
                    <ErrorMessage
                      msg={methods.formState.errors?.carId?.message}
                    />
                  )}
                </Col>
              </Row>
              <Row className="mt-4">
                <Col xs="12">
                  <p className="text-sm">
                    <strong>{t("IMPORTANTE:")}</strong>{t("La contraseña se genera automáticamente y ")} <br /> {t("es enviada al correo una vez finalizada la creación de usuario.")}
                  </p>
                </Col>
              </Row>

              <Row className="mt-4">
                <Col xs="6">
                  <Button
                    color="orange"
                    className={`w-100 bg-[#F8612C] text-white hover:bg-orange-500 ${
                      !isForm1Complete ? "disabled-button" : ""
                    }`}
                    type="button"
                    onClick={handleNextStep}
                    disabled={!isForm1Complete}
                  >
                    {t("Siguiente paso")}
                  </Button>
                </Col>
                <Col xs="6">
                  <Button
                    color="white"
                    className="w-100 text-black border-black"
                    type="button"
                    onClick={() => {closeModal(false); cerrarModal(false)}}
                  >
                    {t("Cancelar")}
                  </Button>
                </Col>
              </Row>
            </>
          )}

          {step === 2 && (
            <>
              <Row className="mt-0">
                <Col xs="12">
                  <Label className="forms-input-title">{t("Nombre")}</Label>
                  <Controller
                    name="namePayPerson"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        className="p-2 px-3 forms-custom-input"
                        placeholder={t("Juan Pérez")}
                        defaultValue=""
                      />
                    )}
                    rules={{ required: t("Ingrese el nombre del repartidor") }}
                  />
                  {methods.formState.errors?.namePayPerson && (
                    <ErrorMessage
                      msg={methods.formState.errors?.namePayPerson?.message}
                    />
                  )}
                </Col>
                <Col xs="12">
                  <Label className="forms-input-title">{t("RUT")}</Label>
                  <Controller
                    name="rutPayPerson"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        className="p-2 px-3 forms-custom-input"
                        placeholder={t("12.345.678-5")}
                        defaultValue=""
                        invalid={!!rutError}
                        aria-invalid={!!rutError}
                        aria-describedby={rutError ? RUT_ERROR_ID : undefined}
                        onBlur={() => {
                          field.onBlur();
                          setRutTouched(true);
                        }}
                      />
                    )}
                    rules={{
                      required: t("Ingrese el RUT del repartidor"),
                    }}
                  />
                  {methods.formState.errors?.rutPayPerson && (
                    <ErrorMessage
                      msg={methods.formState.errors?.rutPayPerson?.message}
                    />
                  )}
                  {rutError && (
                    <p id={RUT_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
                      {t(rutError, { dv: rutCheck.expectedDv })}
                    </p>
                  )}
                </Col>
                <Col xs="12">
                  <Label className="forms-input-title">{t("Tipo de cuenta")}</Label>
                  <Controller
                    name="accountType"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        className="p-2 px-3 forms-custom-input"
                        defaultValue=""
                      >
                        <option value="">{t("Seleccione el tipo de cuenta")}</option>
                        <option value="corriente">{t("Cuenta Corriente")}</option>
                        <option value="vista">{t("Cuenta Vista")}</option>
                        <option value="chequera">{t("Chequera electrónica")}</option>
                        <option value="ahorro">{t("Cuenta de Ahorro")}</option>
                      </Input>
                    )}
                    rules={{ required: t("Seleccione el tipo de cuenta") }}
                  />
                  {methods.formState.errors?.accountType && (
                    <ErrorMessage
                      msg={methods.formState.errors?.accountType?.message}
                    />
                  )}
                </Col>
                <Col xs="12">
                  <Label className="forms-input-title">{t("Banco")}</Label>
                  <Controller
                    name="bank"
                    control={methods.control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="select"
                        className="p-2 px-3 forms-custom-input"
                        defaultValue=""
                      >
                        <option value="">{t("Seleccione el banco")}</option>
                        {bankOptions.map((b) => (
                          <option key={b.value} value={b.value}>
                            {b.label}
                          </option>
                        ))}
                      </Input>
                    )}
                    rules={{ required: t("Seleccione el banco") }}
                  />
                  {methods.formState.errors?.bank && (
                    <ErrorMessage
                      msg={methods.formState.errors?.bank?.message}
                    />
                  )}
                </Col>
              </Row>

              <Row className="mt-4">
                <Col xs="6">
                  <Button
                    color="orange"
                    className={`w-100 bg-[#F8612C] text-white hover:bg-orange-500 ${
                      !isForm2Complete ? "disabled-button" : ""
                    }`}
                    type="submit"
                    disabled={!isForm2Complete}
                  >
                    {edit ? t("Editar usuario") : t("Crear usuario")}
                  </Button>
                </Col>
                <Col xs="6">
                  <Button
                    color="white"
                    className="w-100 text-black border-black"
                    type="button"
                    onClick={() => {closeModal(false); cerrarModal(false)}}
                  >
                    {t("Cancelar")}
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Container>
    </div>
  );
}
